import React from 'react';
import {
  Activity,
  ActivityAssociation,
  Contact,
  ContactPrimaryAssociation,
  DjangoListResponse,
  EActivityType,
  GroupEntityLabelKey,
  TableName,
} from 'v2/models';

import { searchAssociationsApiCallData } from 'v2/utils/apiUtils';
import _ from 'lodash';
import { getContactNameToDisplay } from './ContactUtils';
import { useTranslation } from 'react-i18next';
import { TranslationKeys, PostProcessKeys } from 'translations';

import { ReactComponent as EmailSVG } from 'v2/icons/activityEmail.svg';
import { ReactComponent as MeetingSVG } from 'v2/icons/activityMeeting.svg';
import { ReactComponent as DataEvaluationSVG } from 'v2/icons/activityDataEvaluation.svg';
import { ReactComponent as NoteSVG } from 'v2/icons/activityNote.svg';
import { ReactComponent as ConversationSVG } from 'v2/icons/activityConversation.svg';
import { ReactComponent as FileDeletedSVG } from 'v2/icons/activityFileDeleted.svg';
import { ReactComponent as FileSVG } from 'v2/icons/activityFileCreated.svg';
import { ReactComponent as ProviderSVG } from 'v2/icons/activityProvider.svg';
import { ReactComponent as PersonSVG } from 'v2/icons/person.svg';
import { ClipOverPaper } from 'v2/icons';

export type ActivityTypesTitle =
  | 'Email'
  | 'Meeting'
  | 'Data Evaluation'
  | 'Note'
  | 'Response to Request'
  | 'Deleted file'
  | 'Created file'
  | 'Accepted Proposal'
  | 'Rejected Proposal'
  | 'Data Vendor Created'
  | 'Contact Created'
  | 'Link Created';

type ActivityTypeInfo = {
  title: ActivityTypesTitle;
  icon: React.FC;
};

export type SearchActivityTypes =
  | 'reviewed'
  | 'created'
  | 'archived'
  | 'resubmitted'
  | 'edited'
  | 'approved'
  | 'responded to'
  | 'accepted'
  | 'rejected';

export function useActivityInfo() {
  const { t } = useTranslation();

  const ActivityInfo: { [key: string]: ActivityTypeInfo } = {
    email: { title: 'Email', icon: EmailSVG },
    meeting: { title: 'Meeting', icon: MeetingSVG },
    data_evaluation: { title: 'Data Evaluation', icon: DataEvaluationSVG },
    note: { title: 'Note', icon: NoteSVG },
    responded_to_request: {
      title: 'Response to Request',
      icon: ConversationSVG,
    },
    file_deleted: { title: 'Deleted file', icon: FileDeletedSVG },
    file_created: { title: 'Created file', icon: FileSVG },
    proposal_accepted: {
      title: 'Accepted Proposal',
      icon: ConversationSVG,
    },
    proposal_rejected: {
      title: 'Rejected Proposal',
      icon: ConversationSVG,
    },
    data_vendor_created: {
      title: `${t(TranslationKeys.dataVendor_one, {
        postProcess: PostProcessKeys.TitleCase,
      })} Created` as ActivityTypesTitle,
      icon: ProviderSVG,
    },
    contact_created: {
      title: `${t(TranslationKeys.contact_one, {
        postProcess: PostProcessKeys.TitleCase,
      })} Created` as ActivityTypesTitle,
      icon: PersonSVG,
    },
    [EActivityType.link_created]: {
      title: 'Link Created',
      icon: () => <ClipOverPaper color="var(--primary)" />,
    },
  };

  return ActivityInfo;
}

export const SearchActivityType: Record<string, SearchActivityTypes> = {
  responded_to_request: 'responded to',
  request_created: 'created',
  request_archived: 'archived',
  request_resubmitted: 'resubmitted',
  request_edited: 'edited',
  request_approved: 'approved',
  request_responded_to: 'reviewed',
  proposal_rejected: 'rejected',
  proposal_accepted: 'accepted',
};

export type relatedTableName =
  | 'DataVendor'
  | 'Contact'
  | 'DataProduct'
  | 'Searches';

export type TypeOfAssociation = {
  [key: string]: ActivityAssociation[];
};

export interface PotentialAssociationsQueryResult {
  count: number;
  results: {
    name: string;
    id: number;
    primary_associations?: ContactPrimaryAssociation;
  }[];
}

export type searchAssociationsApiCall =
  searchAssociationsApiCallData<relatedTableName>;

export const mapContactAssociations = (
  contacts: DjangoListResponse<Contact> | undefined
) => {
  return (
    contacts?.results.map((contact) => ({
      table_name: 'Contact',
      name: getContactNameToDisplay(contact),
      object_id: contact.id,
      selected: false,
      editable: true,
    })) || []
  );
};

export const getUniqueAssociations = (associations: ActivityAssociation[]) => {
  return _.uniqBy(associations, (assoc) =>
    [assoc.object_id, assoc.table_name].join()
  );
};

export const groupAssociations = (
  groupEntityLabel: Record<GroupEntityLabelKey, string>,
  associationList: ActivityAssociation[]
) => {
  const groups = Object.entries(groupEntityLabel)
    .filter(([key]) => key !== 'ALL')
    .map(([key, value]) => {
      return {
        type: key,
        label: value,
        data: associationList.filter(
          (association) => association.table_name === key
        ),
      };
    });
  return groups;
};

export const getAssociationLength = (
  associations: Array<ActivityAssociation>,
  type?: string
) => {
  if (!type) {
    const currentVisibleTypes = Object.keys(TableName);
    const visibleAssociations = associations.filter((a) =>
      currentVisibleTypes.includes(a.table_name)
    );
    return visibleAssociations.length;
  }
  return associations.filter((association) => association.table_name === type)
    .length;
};

export const getActivityAssociationName = (
  name: string,
  primary_associations?: ContactPrimaryAssociation
) => {
  const trimmedName = name?.trim();
  return trimmedName || primary_associations?.email?.email || '';
};

export const getActivityTitle = (
  activityTypeTitle: string,
  activity: Activity
) => {
  if (
    activity.type === 'email' &&
    activity.title !== null &&
    activity.title !== ''
  ) {
    return `${activityTypeTitle}: ${activity.title}`;
  } else {
    return activityTypeTitle || activity.type;
  }
};

export const getActivityContactsEmail = (activity: Activity) => {
  const filteredContacts = activity.relationships.filter(
    (relationship) =>
      relationship.table_name === 'Contact' && relationship.name.includes('@')
  );
  return filteredContacts.map((contact) => contact.name);
};
